import { useCallback, useEffect, useRef } from 'react';
import type { ComponentProps } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Typography,
  makeStyles,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import {
  SearchBar,
  SearchResult,
  SearchResultPager,
  useSearch,
} from '@backstage/plugin-search-react';
import { useRouteRef } from '@backstage/core-plugin-api';
import { searchPlugin } from '@backstage/plugin-search';

const useStyles = makeStyles(theme => ({
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  input: { flexGrow: 1 },
  // The results area holds its height whichever of the three states it is in,
  // so the dialog does not jump as the first keystroke lands.
  body: { minHeight: 180 },
  // Only the no-term hint is centred in that space. Results read top-down, and
  // centring a short list leaves it floating in the middle of the dialog.
  bodyCentred: {
    minHeight: 180,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  quiet: {
    color: 'var(--ar-fg-3)',
    textAlign: 'center',
    padding: theme.spacing(4, 2),
  },
}));

/**
 * The search dialog's body, replacing the one `SearchModal` ships.
 *
 * The stock body renders `<SearchResult>` unconditionally, and `SearchResult`
 * shows its no-results empty state whenever the result set is falsy — which
 * includes the state a freshly opened dialog is in, before a term exists and
 * before it has asked the backend anything at all. So opening search and typing
 * nothing greeted you with "Sorry, no results were found": an apology for a
 * question nobody had asked yet, and one that reads as "the catalog is empty".
 *
 * An empty box is not a failed search. Three states, and only one of them is
 * negative:
 *
 *   no term      a quiet line saying what is searchable. Nothing is queried.
 *   term, hits   the results.
 *   term, none   an honest miss, quoting the term back so it is clear WHAT
 *                found nothing.
 *
 * `SearchModal` takes a `children` render prop that replaces its body inside
 * the same `SearchContextProvider`, so this needs no extension override and no
 * fork of the search page — which matters, because `page:search` owns the
 * search plugin's routeRef and the "View full results" link below resolves
 * through it.
 */
export const SearchModalBody = (props: {
  toggleModal: () => void;
  // Taken off `SearchResult` itself rather than restated, because that is where
  // it is going. `SearchModal` hands this through untouched — rendered nodes or
  // a function over the whole result set — so a result-item extension
  // registered elsewhere keeps working through this body.
  resultItemComponents?: ComponentProps<typeof SearchResult>['children'];
}) => {
  const { toggleModal, resultItemComponents } = props;
  const classes = useStyles();
  const navigate = useNavigate();
  const searchRoute = useRouteRef(searchPlugin.routes.root)();
  const { term } = useSearch();
  const barRef = useRef<HTMLInputElement>(null);

  // `SearchModal` mounts this fresh on each open (the dialog only renders its
  // provider while open), so focusing on mount is enough — and it is the whole
  // point of a search dialog that you can type the moment it appears. The stock
  // body does this too; leaving it out made the dialog silently swallow
  // everything typed at it.
  useEffect(() => {
    barRef.current?.focus();
  }, []);

  const viewFullResults = useCallback(() => {
    navigate(`${searchRoute}?query=${encodeURIComponent(term)}`);
    toggleModal();
  }, [navigate, searchRoute, term, toggleModal]);

  return (
    <>
      <DialogTitle>
        <Box className={classes.titleRow}>
          <SearchBar
            className={classes.input}
            inputProps={{ ref: barRef }}
            onSubmit={viewFullResults}
          />
          <IconButton aria-label="close" onClick={toggleModal}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent className={term ? classes.body : classes.bodyCentred}>
        {term ? (
          <>
            <Grid container direction="row-reverse">
              <Grid item>
                <Button
                  color="primary"
                  endIcon={<ArrowForwardIcon />}
                  onClick={viewFullResults}
                  disableRipple
                >
                  View full results
                </Button>
              </Grid>
            </Grid>
            <Divider />
            <SearchResult
              onClick={toggleModal}
              onKeyDown={toggleModal}
              noResultsComponent={
                <Typography className={classes.quiet}>
                  Nothing matches “{term}”.
                </Typography>
              }
            >
              {resultItemComponents}
            </SearchResult>
          </>
        ) : (
          <Typography className={classes.quiet}>
            Search components, APIs, resources, docs and golden paths.
          </Typography>
        )}
      </DialogContent>

      {/* The pager reads the same context, so with no term it has nothing to
          page and renders nothing — but keeping it out of the no-term branch
          entirely means the dialog has no stray empty footer either. */}
      {term && (
        <DialogActions>
          <Grid container direction="row">
            <Grid item xs={12}>
              <SearchResultPager />
            </Grid>
          </Grid>
        </DialogActions>
      )}
    </>
  );
};
